/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: dashboard-core
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../../app/nls/StringResources',
	'../../../app/util/ErrorUtils',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class'
], function (_, stringResources, Utils, UniqueId, BaseClass) {

	let ActionHandler = BaseClass.extend({

		/**
		 * Public API to indicate if the Action should be shown
		 */
		isItemVisible: function (options) {
			return this.canExecute(options);
		},

		/**
		 * Public API to indicate if the Action can be executed
		 */
		canExecute: function (options) {
			const selection = options.target && options.target.activeObject && options.target.activeObject.aSelectedContext;
			const isSelectionAllowed = selection.length === 1 && this._isSelectionValid(options, true);
			return Utils.hasCapability(options.glassContext, 'canAuthorDashboard') && isSelectionAllowed;
		},

		/**
		 * Public API to perform the action
		 */
		doAction: function (context) {
			return this._openDashboardView(context);
		},

		/**
		 * Handle the create from template action
		 */
		onSelectItem: function (context) {
			return this._openDashboardView(context);
		},

		_openDashboardView: function (context) {
			const selection = context.target && context.target.activeObject && context.target.activeObject.aSelectedContext;
			const id = selection.length && selection[0].id;
			return this._getTemplateSpec(context, id).then((spec) => {
				// replace the template's name with the default new dashboard name
				spec = JSON.parse(spec);
				Object.assign(spec, { name: stringResources.get('defaultName') });
				return this._getTargetPerspective().then((perspective) => {
					return context.glassContext.appController.openAppView(perspective, {
						content: {
							id: UniqueId.get('dashboard_'),
							boardSpec: spec,
							isAuthoringMode: true
						}
					}).then((view) => {
						return view.onViewRendered();
					}).then((view) => {
						view.currentContentView.getDashboardApi().getFeature('DashboardState').setDirty(true);
						view.currentContentView.openDatasetpane(false);
					});
				});
			});
		},

		_getTemplateSpec: function (context, id) {
			return context.glassContext.getSvc('.Content').then((contentSvc) => {
				var url = contentSvc.getBaseObjectsURL() + '/' + id;
				var options = {
					dataType: 'json',
					type: 'GET',
					data: {
						fields: 'specification'
					}
				};
				return contentSvc.get(url, options).then((response) => {
					return Promise.resolve(response && response.data && response.data.length && response.data[0].specification);
				});
			});
		},

		_getTargetPerspective: function () {
			return Promise.resolve('dashboard');
		},

		_hasValidTags: function (item) {
			const tags = item.tags || [];
			return tags.includes('dashboard_template');
		},

		_isSelectionValid: function (options, defaultValue) {
			const selection = options.target && options.target.activeObject && options.target.activeObject.aSelectedContext;
			if (selection) {
				for (let item of selection) {
					if (!this._hasValidTags(item)) {
						return false;
					}

					const isDashboardTemplatesEnabled = !options.glassContext.getCoreSvc('.FeatureChecker').checkValue('dashboard', 'dashboardTemplates', 'disabled');
					if (!isDashboardTemplatesEnabled) {
						return false;
					}

					if (item.type !== 'exploration') {
						return false;
					}

					const aPerm = ['read', 'execute'];
					if (_.intersection(item.permissions, aPerm).length < aPerm.length) {
						return false;
					}

					if (item.disabled && item.disabled.toString().toLowerCase() === 'true') {
						return false;
					}
				}
				return true;
			}
			return defaultValue || false;
		}

	});
	return ActionHandler;
});
